import type {NotificationItemData} from '../../types';

import {dateToReadableDate} from '@date-picker';
import {Badge, Button} from '@ds/components';
import {cn} from '@ds/helpers';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from 'store';
import {readNotification} from 'store/slices/notification/thunk';
import {NotificationType} from 'utils/constant';

import {resolveNotificationDeepLink} from './utils';

type NotificationItemProps = {
  notification: NotificationItemData;
  vendorId: string | number;
};

const NotificationItem: React.FC<NotificationItemProps> = ({notification, vendorId}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    id,
    created_at: date,
    title,
    priority,
    type = 'notification',
    body: description,
    seen_at: seenAt,
    read_at: readAt,
    metadata: metaData
  } = notification;

  const handleDetailsClick = () => {
    const deepLink = resolveNotificationDeepLink(metaData ?? {});
    if (!deepLink) {
      return;
    }

    if (readAt === null) {
      void dispatch(readNotification({id, vendor_id: vendorId}));
    }
    navigate(deepLink);
  };

  return (
    <article
      className={cn(
        'flex min-h-25 flex-col gap-2.5 border-b border-gray-03 px-4 py-3.5',
        seenAt ? 'bg-gray-02' : 'bg-white'
      )}
    >
      <time className="text-body-xs text-gray-07">
        {dateToReadableDate({
          date,
          month: true,
          day: true,
          year: true,
          hour: true,
          minute: true
        })}
      </time>

      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {!seenAt && <Badge variant="dot" color="primary" size="md" />}
          <h2 className="truncate text-body-sm-emphasis text-neutral-fg">{title}</h2>
          {priority === 'high' && (
            <Badge variant="status" color="primary" size="sm">
              فوری
            </Badge>
          )}
        </div>
        <Badge variant="status" color="neutral" size="sm">
          {NotificationType[type]}
        </Badge>
      </div>

      <div className="text-body-sm text-gray-08">
        <p>{description}</p>
        {metaData && (
          <Button
            variant="link"
            color={seenAt ? 'success' : 'secondary'}
            size="sm"
            className="h-auto min-h-0 px-0"
            onClick={handleDetailsClick}
          >
            مشاهده جزئیات
          </Button>
        )}
      </div>
    </article>
  );
};

export default NotificationItem;
